import { Heading } from '@mm/components';
import { UsersIcon } from '@mm/icons';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { Contact } from '@mm/state/interfaces/contacts';
import { FilterParams, TagsFilters } from '@mm/state/model/AudiencesModel';
import {
  selectContacts,
  selectContactsByAudienceId,
} from '@mm/state/selectors/contactsSelector';
import { connectedPageSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { getUniqId } from '@mm/utils/src/get-uniq-id';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import { PAGE_SIZE } from '../../Audiences';
import { useFetchAudienceContacts } from '../../use-fetch-audience-contacts';
import './AudiencePreviewLazy.pcss';

type Props = {
  className?: string;
  audienceId: number | undefined;
  usedInAutomation?: boolean;
  contactFilters?: {
    filterParams?: FilterParams;
    tagsFilters?: TagsFilters;
  };
};

export const AudiencePreviewLazy = ({
  className,
  audienceId,
  usedInAutomation,
  contactFilters,
}: Props) => {
  const { fetchMore, isFetching } = useFetchAudienceContacts(audienceId, {
    pageSize: PAGE_SIZE,
    skipBusy: true,
    pauseRefreshingWhenEditingAudience: true,
  });
  const { retrieveAll, resetAllContacts } = useContactsCRUDActions();
  const [retrievingContacts, setRetrievingContacts] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);

  const connectedPage = useSelector(connectedPageSelector);
  const contactsByAudienceId = useSelector(state =>
    selectContactsByAudienceId(state, audienceId),
  );

  const allContacts = useSelector(
    (state: StoreState) => state.contacts.allContacts,
  );
  const stateContacts = useSelector(selectContacts);
  const audienceSavePending = Boolean(
    contactFilters?.tagsFilters || contactFilters?.filterParams,
  );
  const getContactsFromAudience = audienceId && !audienceSavePending;

  const contacts = getContactsFromAudience
    ? contactsByAudienceId?.contacts
    : stateContacts.contacts;
  const pagination =
    getContactsFromAudience && !audienceSavePending
      ? contactsByAudienceId?.pagination
      : stateContacts.pagination;

  const contactsByAudience =
    audienceId && !audienceSavePending ? contacts : allContacts;

  const count = pagination?.total;
  const noResults = !count;

  const hasMore = !pagination?.isLastPage;
  const nextPage = Number(pagination?.page) + 1 || 1;

  const getAllContacts = async (
    pageNumber: number,
    contactFilters?: {
      filterParams?: FilterParams;
      tagsFilters?: TagsFilters;
    },
  ) => {
    if (connectedPage) {
      setRetrievingContacts(true);
      await retrieveAll(
        connectedPage.id,
        pageNumber,
        PAGE_SIZE,
        undefined,
        {
          skipBusy: true,
          audience: contactFilters,
        },
        true,
      );
      setRetrievingContacts(false);
    }
  };

  const onMoreRequest = async () => {
    if (audienceId && !audienceSavePending) {
      setFetchingMore(true);
      await fetchMore(pagination);
      setFetchingMore(false);
    }

    if ((!audienceId || audienceSavePending) && hasMore && connectedPage) {
      setFetchingMore(true);
      await getAllContacts(nextPage, contactFilters);
      setFetchingMore(false);
    }
  };

  useEffect(() => {
    if (audienceSavePending || !audienceId) {
      resetAllContacts();
      getAllContacts(1, contactFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactFilters?.filterParams, contactFilters?.tagsFilters]);

  const generateFirstName = (contact: Contact) => {
    if (contact.firstName && contact.username) {
      if (contact.firstName === 'Instagram Contact' && contact.username) {
        return `@${contact.username}`;
      }
      return `${contact.firstName}`;
    }
    if (contact.firstName) {
      return `${contact.firstName}`;
    }
    if (contact.username) {
      return `@${contact.username}`;
    }
  };

  const generateLastName = (contact: Contact) => {
    if (contact.lastName) {
      if (contact.lastName === 'IG') {
        return null;
      }
      return `${contact.lastName}`;
    }
    return null;
  };

  const isBusy = isFetching || retrievingContacts;

  return (
    <div className={classNames(className, 'audience-preview-lazy')}>
      {!usedInAutomation && (
        <div className="audience-preview-lazy__topbar">
          <UsersIcon className="audience-preview-lazy__contacts_icon" />
          <Heading size={4} text="Matching contacts" />
          <div className="audience-preview-lazy__contacts-counter">{count}</div>
        </div>
      )}
      <div className="audience-preview-lazy__contact audience-preview-lazy__contacts-header">
        <span className="audience-preview-lazy__contact-data">First Name</span>
        <span className="audience-preview-lazy__contact-data">Last Name</span>
      </div>
      <section className="audience-preview-lazy__contacts">
        {noResults && !isBusy && (
          <span className="audience-preview-lazy__empty-state-message">
            No matching contacts
          </span>
        )}
        {!noResults &&
          (!isBusy || (isBusy && fetchingMore)) &&
          contactsByAudience?.map(c => (
            <div className="audience-preview-lazy__contact" key={getUniqId()}>
              <span className="audience-preview-lazy__contact-data">
                {generateFirstName(c)}
              </span>
              <span className="audience-preview-lazy__contact-data">
                {generateLastName(c)}
              </span>
            </div>
          ))}
        {hasMore && !isBusy && (
          <Waypoint bottomOffset="-20px" onEnter={onMoreRequest} />
        )}
        {(noResults || hasMore) && isBusy && (
          <div className="audience-preview-lazy__loading-indicator">
            <span>Loading...</span>
          </div>
        )}
      </section>
    </div>
  );
};
